import { SyncError } from "../errors.js";
import { normalizeOpenRouterModel } from "../models/normalizeOpenRouterModel.js";
import type { AiModelRecord } from "../models/types.js";
import type {
  OpenRouterModelsQuery,
  OpenRouterModelsResponse,
} from "../models/openrouter.types.js";

export type OpenRouterSyncProviderOptions = {
  /** Optional — public GET /models needs no key. */
  apiKey?: string;
  baseUrl?: string;
  /** Query params passed to OpenRouter (default: all modalities). */
  query?: OpenRouterModelsQuery;
};

function buildFetchHeaders(apiKey?: string): Record<string, string> {
  const headers: Record<string, string> = { Accept: "application/json" };
  if (apiKey?.trim()) headers.Authorization = `Bearer ${apiKey.trim()}`;
  return headers;
}

function buildModelsUrl(baseUrl: string, query?: OpenRouterModelsQuery): string {
  const url = new URL(`${baseUrl.replace(/\/$/, "")}/models`);
  const q = { output_modalities: "all", ...query };
  for (const [key, value] of Object.entries(q)) {
    if (value !== undefined && value !== "") url.searchParams.set(key, value);
  }
  return url.toString();
}

export class OpenRouterSyncProvider {
  private readonly apiKey?: string;
  private readonly baseUrl: string;
  private readonly query?: OpenRouterModelsQuery;

  constructor(options: OpenRouterSyncProviderOptions = {}) {
    this.apiKey = options.apiKey;
    this.baseUrl = options.baseUrl ?? "https://openrouter.ai/api/v1";
    this.query = options.query ?? { output_modalities: "all" };
  }

  /**
   * Fetches the full OpenRouter model catalog (public, no API key required).
   * @see https://openrouter.ai/api/v1/models
   */
  async fetchModels(): Promise<AiModelRecord[]> {
    const url = buildModelsUrl(this.baseUrl, this.query);
    let response: Response;

    try {
      response = await fetch(url, { headers: buildFetchHeaders(this.apiKey) });
    } catch (cause) {
      throw new SyncError("OPENROUTER_MODELS_FETCH_FAILED", `Failed to fetch OpenRouter models from ${url}`, cause);
    }

    if (response.status === 401 || response.status === 403) {
      throw new SyncError(
        "OPENROUTER_AUTH_FAILED",
        this.apiKey
          ? "OpenRouter API key is invalid or unauthorized."
          : "OpenRouter returned unauthorized — remove OPENROUTER_API_KEY to use the public models endpoint.",
      );
    }

    if (!response.ok) {
      const body = await response.text().catch(() => "");
      throw new SyncError(
        "OPENROUTER_MODELS_FETCH_FAILED",
        `OpenRouter models fetch failed (${response.status}): ${body.slice(0, 200)}`,
      );
    }

    const json = (await response.json()) as OpenRouterModelsResponse;
    const syncedAt = new Date().toISOString();
    return (json.data ?? []).map((row) => normalizeOpenRouterModel(row, syncedAt));
  }

  /** Build the URL used for the last fetch pattern (for debugging). */
  getModelsUrl(): string {
    return buildModelsUrl(this.baseUrl, this.query);
  }
}
